import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { insights } from "../content/insights";

/**
 * Browser-safe front-matter handling:
 * - Supports YAML front-matter wrapped in --- ... ---
 * - Also strips accidental "key: value" header lines at the top (non-standard)
 * - Parsing is intentionally minimal (strings only), but safe and sufficient for title/date/excerpt/category.
 */
function parseFrontMatterAndContent(raw) {
  if (!raw) return { data: {}, content: "" };

  const text = raw.replace(/^\uFEFF/, ""); // remove BOM if present

  // Case 1: YAML front matter
  if (text.startsWith("---\n") || text.startsWith("---\r\n")) {
    const end = text.indexOf("\n---", 4);
    const endCRLF = text.indexOf("\r\n---", 4);
    const fmEnd = end !== -1 ? end : endCRLF;

    if (fmEnd !== -1) {
      // find the end marker line break
      const after = text.indexOf("\n", fmEnd + 1);
      const after2 = text.indexOf("\r\n", fmEnd + 1);
      const afterEnd = after !== -1 ? after : after2;

      const fmBlock = text.slice(4, fmEnd).trim();
      const content = (afterEnd !== -1 ? text.slice(afterEnd + 1) : "").trim();

      const data = parseSimpleYamlBlock(fmBlock);
      return { data, content };
    }
  }

  // Case 2: Non-standard header lines like:
  // title: "..." date: "..." excerpt: "..." category: "..."
  // (as seen in your screenshot) — strip the first line if it looks like metadata.
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const looksLikeMeta =
    /(^|\s)(title|date|excerpt|category)\s*:\s*/i.test(firstLine) &&
    firstLine.length < 300;

  if (looksLikeMeta) {
    const rest = text.split(/\r?\n/).slice(1).join("\n").trim();
    const data = parseInlineKeyValueLine(firstLine);
    return { data, content: rest };
  }

  return { data: {}, content: text.trim() };
}

function parseSimpleYamlBlock(block) {
  // Minimal YAML: "key: value" per line, string values with or without quotes.
  // Good enough for title/date/excerpt/category.
  const data = {};
  const lines = block.split(/\r?\n/);

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;

    const idx = trimmed.indexOf(":");
    if (idx === -1) continue;

    const key = trimmed.slice(0, idx).trim();
    let value = trimmed.slice(idx + 1).trim();

    // strip quotes
    value = value.replace(/^"(.*)"$/, "$1").replace(/^'(.*)'$/, "$1");

    if (key) data[key] = value;
  }

  return data;
}

function parseInlineKeyValueLine(line) {
  // Very simple "key: value" extractor from one line
  // Supports quoted values; ignores unknown keys.
  const data = {};
  const keys = ["title", "date", "excerpt", "category"];

  for (const k of keys) {
    const re = new RegExp(`${k}\\s*:\\s*("([^"]*)"|'([^']*)'|([^\\s]+))`, "i");
    const m = line.match(re);
    if (m) {
      data[k] = (m[2] ?? m[3] ?? m[4] ?? "").trim();
    }
  }

  return data;
}

export default function InsightArticle() {
  const { slug } = useParams();

  const entry = useMemo(() => insights.find((x) => x.slug === slug), [slug]);

  const [md, setMd] = useState("");
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setNotFound(false);
      setMd("");
      setMeta(null);

      if (!entry?.url) {
        setNotFound(true);
        return;
      }

      try {
        const res = await fetch(entry.url);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);

        const raw = await res.text();
        if (cancelled) return;

        // Parse front-matter safely (never throws in a way that flips to notFound)
        const { data, content } = parseFrontMatterAndContent(raw);

        const mergedMeta = {
          title: data.title || entry.title,
          date: data.date || entry.date,
          excerpt: data.excerpt || entry.excerpt,
          category: data.category || entry.category,
        };

        setMeta(mergedMeta);
        setMd(content || "");
      } catch (e) {
        if (!cancelled) setNotFound(true);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [entry]);

  if (notFound) {
    return (
      <main className="container mx-auto px-4 py-10 max-w-4xl">
        <h1 className="text-2xl font-semibold mb-4">Article not found</h1>
        <p className="mb-6">
          The insight you’re looking for doesn’t exist (or has been moved).
        </p>
        <Link className="underline underline-offset-4" to="/resources">
          Back to Insights &amp; Resources
        </Link>
      </main>
    );
  }

  if (!md) {
    return (
      <main className="container mx-auto px-4 py-10 max-w-4xl">
        <p>Loading…</p>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-10 max-w-4xl print:py-0 print:max-w-none">
      {/* Luxe header band (aligned with ClientResources style) */}
      <header className="relative mb-10">
        <div
          className={[
            "absolute inset-x-0 -top-6 -bottom-4",
            "rounded-3xl border",
            "bg-gradient-to-br from-transparent via-background to-transparent",
            "shadow-sm",
            "pointer-events-none",
          ].join(" ")}
          aria-hidden="true"
        />

        <div className="relative px-5 sm:px-6 py-5 sm:py-6">
          <Link className="text-sm underline underline-offset-4" to="/resources">
            ← Insights &amp; Resources
          </Link>

          <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
            {meta?.title ?? entry.title}
          </h1>

          {(meta?.category || meta?.date) && (
            <p className="mt-3 text-sm text-muted-foreground">
              {meta?.category ? `${meta.category} • ` : ""}
              {meta?.date ?? ""}
            </p>
          )}

          {meta?.excerpt && (
            <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-3xl">
              {meta.excerpt}
            </p>
          )}
        </div>
      </header>

      {/* Article body */}
      <article className="prose max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
      </article>
    </main>
  );
}
